import { CharacterState, characterStateOf } from "../combat/anim.ts";

/**
 * The stance a watching page draws another character in (ADR-094 decision 22). A watcher is told no stance: the
 * stance is each page's own intent and nothing of it is on the wire. So a watching page draws another character's
 * weapon from any act it sees start (a swing, a hurt, a dash) until `WATCHED_STANCE_MS` pass without another, and put
 * away otherwise. It costs nothing on the wire and cannot be used to hide: every act that matters draws the weapon on
 * every screen.
 *
 * An act is read as the character's strips are (`sprite/playback.ts`): from a change of its animation bits, the
 * restart bit alone counting. The first byte seen draws nothing, since a state stays in the bits long after its strip
 * has played. A death puts the weapon away at once, so a character that stands up again stands with it away.
 */

/**
 * How long another character's weapon stays drawn after the last act a watcher saw it start, in ms. Tunable.
 */
export const WATCHED_STANCE_MS = 10_000;

/**
 * The derived stance of one character on a watching page. `observe` allocates nothing: a page calls it for every
 * other player each frame.
 */
export class WatchedStance {
  private lastByte = -1;
  private until = Number.NEGATIVE_INFINITY;

  /**
   * Reads the animation bits the character's snapshot carries this frame.
   *
   * @param anim The six animation bits.
   * @param nowMs Scene time in ms.
   * @returns True while its weapon is drawn.
   */
  public observe(anim: number, nowMs: number): boolean {
    if (this.lastByte < 0) {
      this.lastByte = anim;
      return false;
    }
    if (anim !== this.lastByte) {
      this.lastByte = anim;
      const state = characterStateOf(anim);
      if (state === CharacterState.Attack1 || state === CharacterState.Attack2 || state === CharacterState.Hurt || state === CharacterState.Dash) {
        this.until = nowMs + WATCHED_STANCE_MS;
      } else if (state === CharacterState.Dead) {
        this.until = Number.NEGATIVE_INFINITY;
      }
    }
    return nowMs < this.until;
  }

  /**
   * Forgets the last byte observed and puts the weapon away, so the next byte is first sight.
   */
  public forget(): void {
    this.lastByte = -1;
    this.until = Number.NEGATIVE_INFINITY;
  }
}
